import type { InstitutionTypeOption } from '@/lib/services/institutions/types';
import { LIABILITY_TYPE_SLUGS } from '@/lib/services/inventory-items/types';
import type { InlineCreateSelectOption } from './inline-create-step';

/**
 * Which side of the asset/liability split the picker is offering types for
 * (VALE-1595). The "Other" picker mints an institution for one item at a time, and
 * that item is always squarely one or the other — an asset drawer never mints a
 * liability-only institution and vice versa.
 */
export type InstitutionTypeSide = 'asset' | 'liability';

/**
 * **What each institution type covers, derived from the registry** (VALE-1577).
 *
 * The create step offers a type, and the type vocabulary is opaque to a lawyer who
 * does not already hold it: "Aged Care Provider" is where a nursing home bond sits,
 * but the label alone never says so. So every option carries a line naming the
 * inventory items that type holds.
 *
 * **Derived, never restated.** `inventoryItemTypes` on each type is the registry's
 * own mapping (`backend/src/institutions/constants/institution-types.constants.ts`,
 * served by `GET /institution-types`), so a type added there — or an item type moved
 * between types — reaches this selector with no change here. A hand-written label
 * map is the thing this function exists instead of: it would be correct until the
 * registry moved, and wrong silently afterwards.
 */

/**
 * Item-type slugs that name no particular asset.
 *
 * Two registry entries map to nothing else — `other` itself, and `health-insurance`,
 * whose matter-side record is a premium refund (`other`) or an arrears debt
 * (`other-liability`) rather than a type of its own. Rendering their mapping
 * verbatim produces "Other, Other Liability", which tells the reader less than
 * nothing: for `other` it is circular, and for a health insurer it hides what the
 * type is actually for. They fall through to {@link GENERIC_COVERAGE} instead.
 */
const GENERIC_ITEM_TYPE_SLUGS: ReadonlySet<string> = new Set([
  'other',
  'other-liability'
]);

/**
 * Shown when the registry's mapping resolves to nothing specific.
 *
 * Phrased to describe the **bucket**, not the type, because two types land here and
 * a sentence true of one is false of the other: `other` really is "anything without
 * a more specific type", but `health-insurance` is not — a health fund is a specific
 * thing whose matter-side record merely happens to be a general one (a premium
 * refund, or arrears). Naming what the registry actually says about them both — that
 * the item is recorded generally — is the honest reading; inventing a per-type
 * sentence here would be the restatement this module exists instead of.
 */
const GENERIC_COVERAGE = 'Recorded as a general asset or liability';

/**
 * What the create step pre-selects.
 *
 * `other` is what this picker already mints for every organisation today
 * (`InventoryItemTypeSlug.OTHER` → `InstitutionTypeSlug.OTHER`), so pre-selecting
 * it is what makes the selector purely additive: a reader who never opens it gets
 * exactly the institution they got before.
 */
export const DEFAULT_NEW_INSTITUTION_TYPE = 'other';

/**
 * The line under one type's label — the item types it holds, in the registry's own
 * order, or {@link GENERIC_COVERAGE} when that resolves to nothing specific.
 */
export function describeInstitutionTypeCoverage(
  type: Pick<InstitutionTypeOption, 'inventoryItemTypes'>
): string {
  const specific = (type.inventoryItemTypes ?? [])
    .filter((itemType) => !GENERIC_ITEM_TYPE_SLUGS.has(itemType.slug))
    .map((itemType) => itemType.name);

  return specific.length > 0 ? specific.join(', ') : GENERIC_COVERAGE;
}

/**
 * Whether a type belongs on the given side, derived from the same registry
 * mapping {@link describeInstitutionTypeCoverage} reads (VALE-1595) — never a
 * hand-maintained list. A type holding both an asset and a liability item type
 * (`other`, `health-insurance`) belongs on both sides; a type holding only
 * liability item types (`lender`, `tax-authority`, `funeral-service`,
 * `utility-provider`, `professional-service`) is liability-only and must not
 * reach an asset picker's create step, and vice versa.
 */
function appliesToSide(
  type: Pick<InstitutionTypeOption, 'inventoryItemTypes'>,
  side: InstitutionTypeSide
): boolean {
  return (type.inventoryItemTypes ?? []).some((itemType) =>
    side === 'liability'
      ? LIABILITY_TYPE_SLUGS.has(itemType.slug)
      : !LIABILITY_TYPE_SLUGS.has(itemType.slug)
  );
}

/**
 * The registry's types as selector options, each described by what it covers —
 * scoped to the side the picker is offering types for (VALE-1595), so a picker on
 * an asset item never offers a liability-only type and vice versa.
 *
 * Order is the server's (`GET /institution-types` sorts by name) and is deliberately
 * not re-sorted here — one ordering, defined once, in the place that already owns it.
 */
export function toInstitutionTypeOptions(
  types: InstitutionTypeOption[] | undefined,
  side: InstitutionTypeSide
): InlineCreateSelectOption[] {
  return (types ?? [])
    .filter((type) => appliesToSide(type, side))
    .map((type) => ({
      value: type.slug,
      label: type.name,
      description: describeInstitutionTypeCoverage(type)
    }));
}
